import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { ManagementsRequestStateProps } from "../../../../components/Managements/ManagementsRequestState/ManagementsRequestState";
import {
  ManagementRequestInfo,
  PostCallCenterRequestBody,
} from "../../../models/managements/managements.model";
import { OwnerDataCups } from "../../../models/ownerData/ownerData.model";
import { initialOwnerDataCup } from "../../../../utils/ownerData/ownerData.util";
import TotalEnergiesContext from "../../../store/context/TotalEnergiesContext";
import {
  postCallCenter,
  postImportDocument,
} from "../../../services/managements/managements.service";
import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
} from "../../../constants/constants";
import { formatDate } from "../../../../utils/dateUtils/formatDate.util";
import { initialManagementRequestInfo } from "../../../../utils/components/managements/managements.util";
import { ManagementAgentConstantData } from "../../../models/managements/managements.agent.model";

export interface ManagementsRequestProps
  extends Omit<ManagementsRequestStateProps, "managementRequestInfo"> {
  managementRequestInfo: ManagementRequestInfo;
}

const MANAGEMENTS_REQUEST_INITIAL_STATE: ManagementsRequestProps = {
  state: "",
  title: "",
  description: "",
  descriptionExtra: "",
  isLoading: true,
  showManagementRequestInfo: true,
  managementRequestInfo: initialManagementRequestInfo as ManagementRequestInfo,
};

export default function useCallCenterManagement(
  managementServiceConstants: ManagementAgentConstantData
) {
  const { ownerData, userData, isAgent } = useContext(TotalEnergiesContext);

  const [file, setFile] = useState<File | null>(null);

  const [step, setStep] = useState(0);

  const [selectedDeliveryAddress, setSelectedDeliveryAddress] =
    useState<OwnerDataCups>(initialOwnerDataCup);

  const [additionalText, setAdditionalText] = useState<string>("");

  const [managementsRequestStateProps, setManagementsRequestStateProps] =
    useState<ManagementsRequestProps>(MANAGEMENTS_REQUEST_INITIAL_STATE);

  const postCallCenterAbortController = new AbortController();

  useEffect(() => {
    return () => {
      postCallCenterAbortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCallCenter = useCallback(
    (body: PostCallCenterRequestBody, token: string): void => {
      setStep(1);

      postCallCenter(body, token, postCallCenterAbortController.signal)
        .then((response) => {
          if (isEmpty(response)) {
            throw new Error("Empty response from postCallCenter");
          }

          const [{ IdCallCenter, IdCarpetaIncidencias, Cups, EntornoBD }] =
            response;
          return {
            IdCallCenter,
            IdCarpetaIncidencias,
            Cups,
            EntornoBD,
          };
        })
        .then(
          async ({ IdCallCenter, IdCarpetaIncidencias, Cups, EntornoBD }) => {
            if (IdCallCenter === 0) {
              throw new Error("One or more IdCallCenter values are 0");
            }

            const address = ownerData?.cups.find(
              (item) => item.codeCups === Cups
            )?.address;

            if (file) {
              await postImportDocument(
                IdCallCenter,
                IdCarpetaIncidencias,
                file,
                token
              );
            }
            const managementRequestInfo: ManagementsRequestProps["managementRequestInfo"] =
              {
                ...managementsRequestStateProps.managementRequestInfo,
                cupsInfo: {
                  cupsCode: Cups,
                  address: address ?? "-",
                  environment: EntornoBD,
                },
                refferenceNumber: IdCallCenter.toString(),
                registrarionDate: formatDate(new Date()),
              };
            setManagementsRequestStateProps((prevState) => ({
              ...prevState,
              state: MANAGEMENTS_STATE_PENDING,
              title: "Gestiones.Estamos procesando su solicitud",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
              managementRequestInfo,
              showManagementRequestInfo: true,
              cupsInfo: managementRequestInfo.cupsInfo,
              isLoading: false,
            }));
          }
        )
        .catch((error) => {
          setManagementsRequestStateProps({
            ...MANAGEMENTS_REQUEST_INITIAL_STATE,
            state: MANAGEMENTS_STATE_ERROR,
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
            isLoading: false,
          });
        });
    },
    [
      file,
      managementsRequestStateProps.managementRequestInfo,
      ownerData?.cups,
      postCallCenterAbortController.signal,
    ]
  );

  const handleOnSubmit = useCallback(() => {
    if (userData) {
      const isGas = selectedDeliveryAddress.envirorment.toLowerCase() === "gas";

      const serviceConstants = isGas
        ? managementServiceConstants.servicePayloadData.GAS
        : managementServiceConstants.servicePayloadData.ELECTRICITY;

      const text = `${serviceConstants.managementText}${
        isEmpty(additionalText) ? "" : `: ${additionalText}`
      }`;
      fetchCallCenter(
        {
          IdTipoRegistro: serviceConstants.registerTypeId,
          IdSubTipoReclamacion: serviceConstants.subTypeReclamationId,
          idUsuarioDestino: serviceConstants.userDestinationId,
          TextoIncidencia: text,
          CodigosContratos: null,
          IdsContratos: [Number(selectedDeliveryAddress.idContract)],
        },

        userData.token
      );
    }
  }, [
    additionalText,
    fetchCallCenter,
    managementServiceConstants.servicePayloadData.ELECTRICITY,
    managementServiceConstants.servicePayloadData.GAS,
    selectedDeliveryAddress.envirorment,
    selectedDeliveryAddress.idContract,
    userData,
  ]);

  const cupsOptions = useMemo(() => ownerData?.cups || [], [ownerData?.cups]);

  return {
    setFile,
    file,
    step,
    managementsRequestStateProps,
    onSubmit: handleOnSubmit,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    cupsOptions,
    additionalText,
    setAdditionalText,
  };
}
